import * as React from 'react';
import { autobind } from 'core-decorators';
import {
  IExternalIncidentParams, ExternalIncidentsApi, ITabField, RESOURCE_FIELD_TYPE, UserApi, IUserElement,
  IFieldsPostBody, IFieldData, IVisibilityRulesData,
  RESOURCE_VISIBILITY_TYPE, ITestRequestBody, ITab, FileApi, ITableItem, ITransitionFileResponse, IPickListItem,
  PREDEFINED_FIELDS,
  ICountryObject,
  IExternalStatesParams,
  IStateObject,
  VisibilityUtils,
  formatFormDataForSubmit,
  displayLocalTime, SETTINGS,
  IFieldValueObject,
  FILES
} from '@logicmanager/common';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { IFieldValueEditable, IFieldsMeta, STATUS } from '../../interfaces/state';
import { WebformView } from './webform-view';
import { Prompt } from 'react-router-dom';
import { WEBFORM_TEXT } from '../../constants';
import { DATA_TEST_ID } from '../data-test-id';
import { IWebformProps } from './interfaces';
import './webform.less';
import { formHasBeenEdited } from './webform-utils';
import { getValidatedFields } from '../../utils';

export class ExternalWebform extends React.Component<IWebformProps> {
    public FILE_KEY: string = 'file';
    public REPORTED_ON: string = 'Reported On';
    private VISIBILITY_DEBOUNCE_TIME: number = 175;
    private LOCAL_DATE: string = displayLocalTime(new Date(), 'YYYY-MM-DD');
    // country codes to get states from:
    private STATE_DROPDOWN_COUNTRIES: string = 'US,CA';
    private hasVisibilityRules: boolean;
    private hasUserLookup: boolean;
    private hasStates: boolean;
    private hasCountries: boolean;
    constructor(props: any) {
        super(props);
        this.hasVisibilityRules = false;
        this.hasUserLookup = false;
        this.hasStates = false;
        this.hasCountries = false;
        this.getVisibilityRules = debounce(this.getVisibilityRules, this.VISIBILITY_DEBOUNCE_TIME);
    }

    // TODO: This information must be from server
    /* Store all field meta information into state. i.e field meta data and default values */

    @autobind
    public loadMetaInformation(tabs: ITab[], params: IExternalIncidentParams): void {
        for (let i: number = 0; i < tabs.length; i++) {
            const tab: ITab = tabs[i];
            for (let j: number = 0; j < tab.fields.length; j++) {
                const field: ITabField = tab.fields[j] as ITabField;
                if (field.fieldType === RESOURCE_FIELD_TYPE.PICK_LIST && field.isConditional) {
                    this.loadConditionalField(field);
                }
                this.handleLoadDefaults(field);
            }
            this.props.addTab({
                ...tab,
                fields: map(tab.fields as ITabField[], 'id') as string[]
            });
            this.props.setTabStatus(tab.id, tab.opened);
        }
        if (!this.hasVisibilityRules) {
            this.props.noVisibilityRules();
        }
        if (this.hasUserLookup) {
            this.loadUsers(params);
        }
        if (this.hasCountries) {
          this.loadCountries(params);
        }
        if (this.hasStates) {
          this.loadStates({...params, countryCodes: this.STATE_DROPDOWN_COUNTRIES});
        }
    }

    @autobind
    public loadConditionalField(field: ITabField): void {
      if (field.predefined && field.title && field.title.toLowerCase() === PREDEFINED_FIELDS.REPORTED_TYPE) {
        const defaultChoice: IPickListItem =
          field.choiceItems.find((choice: IPickListItem) => choice.id.toString() === field.value);
        defaultChoice.logicItems.forEach((item: ITabField) => {
          this.handleLoadDefaults(item);
          });
        return;
      }
      field.choiceItems.forEach((choice: IPickListItem) => {
        if (choice.logicItems) {
          choice.logicItems.forEach((item: ITabField) => {
            this.handleLoadDefaults(item, true);
          });
        }
      });
    }

    @autobind
    public handleLoadDefaults(field: ITabField, hidden: boolean = false): void {
      const {id, tabId, fieldType, title, value, userType, visibilityType} = field;
      if (!hidden && visibilityType === RESOURCE_VISIBILITY_TYPE.RULE) {
        this.hasVisibilityRules = true;
      }
      if (fieldType === RESOURCE_FIELD_TYPE.TABLE) {
        this.loadDefaultTableValues(field);
      } else {
        if (fieldType === RESOURCE_FIELD_TYPE.USER_LOOKUP) {
          this.hasUserLookup = true;
        }
        if (fieldType === RESOURCE_FIELD_TYPE.COUNTRY) {
          this.hasCountries = true;
        }
        if (fieldType === RESOURCE_FIELD_TYPE.STATE) {
          this.hasStates = true;
        }
        this.props.loadDefaultValue({
          id,
          tabId,
          fieldType,
          value: (field.predefined && title === this.REPORTED_ON) ? this.LOCAL_DATE : value || '',
          valid: true,
          incomplete: false,
          visible: !hidden && visibilityType && visibilityType === RESOURCE_VISIBILITY_TYPE.VISIBLE
        });
        if (userType) {
          this.props.updateLinkType(id, userType);
        }
      }
      (field.predefined && title === this.REPORTED_ON)
        ? this.props.addField({ ...field, value: this.LOCAL_DATE })
        : this.props.addField(field);
    }

    @autobind
    public async loadUsers(params: IExternalIncidentParams): Promise<void> {
        const users: IUserElement = await UserApi.getExternal(params).request();
        this.props.addUsers(users);
    }

    @autobind
    public async loadCountries(params: IExternalIncidentParams): Promise<void> {
        const countries: ICountryObject[] = await ExternalIncidentsApi.getCountries(params).request();
        this.props.addCountries(countries);
    }

    @autobind
    public async loadStates(params: IExternalStatesParams): Promise<void> {
        const states: IStateObject[] = await ExternalIncidentsApi.getStates(params).request();
        this.props.addStates(states);
    }

    @autobind
    public loadDefaultTableValues(tableField: ITabField): void {
      const tableItems: ITableItem[] = [];
      let index: number = 0;
      for (let i: number = 0; i < tableField.tableItems.length; i++) {
        const fieldType: RESOURCE_FIELD_TYPE = tableField.tableItems[i].fieldType;
        if (fieldType === RESOURCE_FIELD_TYPE.COUNTRY) {
          this.hasCountries = true;
        }
        if (fieldType === RESOURCE_FIELD_TYPE.STATE) {
          this.hasStates = true;
        }
        for (let j: number = 0; j < tableField.size; j++) {
          tableItems[index] = {
              id: tableField.tableItems[i].id,
              col: i,
              row: j,
              valid: true,
              value: '',
              visible: true
          };
          index++;
        }
      }
      this.props.loadDefaultTableValues(
          tableField.id,
          tableField.tabId,
          tableField.required,
          tableItems,
          tableField.visibilityType === RESOURCE_VISIBILITY_TYPE.VISIBLE
      );
    }

    @autobind
    public handleCaptchaVerify(response: string): void {
        this.props.updateCaptchaResponse(response);
    }

    @autobind
    public handleCaptchaExpiration(): void {
        // remove any old captcha response which has now expired
        this.props.updateCaptchaResponse(null);
    }

    @autobind
    public handleSubmit(): void {
        const captchaResponse: string = this.props.values.captchaResponse;
        // captcha is valid if it is either disabled or enabled and truthy
        const isCaptchaValid: boolean = !this.props.ui.isCaptchaEnabled || !!captchaResponse;
        const fieldsMeta: IFieldsMeta = this.props.meta.fields as IFieldsMeta;
        const fieldValuesToSubmit: IFieldValueEditable =
          getValidatedFields(fieldsMeta, { ...this.props.values.fields },
            this.props.updateValue, this.props.setTabStatus, isCaptchaValid);
        if (!fieldValuesToSubmit) { return; }
        this.props.setSubmitted(true);
        const data: IFieldData[] = formatFormDataForSubmit(fieldValuesToSubmit);
        const payload: IFieldsPostBody = {
            fields: data,
            captchaResponse
        };
        this.props.fetchingRequest();
        ExternalIncidentsApi.saveFields(this.props.params, payload).request()
            .then((res: Promise<any>) => {
              this.props.history.push({
                pathname: '/success',
                search: `t=${this.props.params.test}&p=${this.props.params.plan}&k=${this.props.params.key}`,
                text: res ? `Thank you for your submission! Your Event ID is ${res}.` : 'Thank you for your submission!'
              });
              return;
          })
          .catch(() => {
              this.props.history.push('/error');
          });
    }

  @autobind
  public async getVisibilityRules(): Promise<void> {
    const payload: IFieldData[] = VisibilityUtils.formatDataForVisibilityRules(this.props.values.fields);
    const rules: IVisibilityRulesData[] =
      await ExternalIncidentsApi.saveVisibilityRules(this.props.params, payload).request();
    rules.forEach((rule: IVisibilityRulesData) => {
      if (this.props.values.fields[rule.id] && this.props.values.fields[rule.id].visible !== rule.visible) {
        this.props.updateValue(rule.id.toString(), {
          visible: rule.visible
        });
      }
    });
  }

    @autobind
    public async getSettings(): Promise<void> {
        const settings: {} =
            await ExternalIncidentsApi.getSettings(this.props.params).request();
        if (!isNil(settings)) {
            this.props.setSettings(settings);
        }
        let logoUrl: string = '';
        if (settings[SETTINGS.USE_HEADER_LOGO] === '1') {
          await FileApi.getHeaderLogo().then((data: Blob) => {
            logoUrl = URL.createObjectURL(data);
          });
        }
        this.props.setLogoUrl(logoUrl);
    }

    @autobind
    public downloadFile(fieldId: string): void {
      FileApi.getExternalTemplate({...this.props.params, fieldId});
    }

    public UNSAFE_componentWillMount(): void {
        this.props.setSubmitted(false);
    }

    @autobind
    public async saveTemplateFile(file: File, id: string): Promise<void> {
      const requestBody: FormData = new FormData();
      requestBody.append(this.FILE_KEY, file);
      const fileInfo: ITransitionFileResponse
          = await FileApi.createExternalTransitionFile(this.props.params, requestBody);
      this.props.updateValue(id, {
          value: fileInfo.fileName,
          valid: true,
          extra: fileInfo.fileIdentity
      });
    }

  @autobind
  public async saveTemplateFiles(files: File[], id: string): Promise<void> {
    try {
      const fieldValue: IFieldValueObject = this.props.values.fields[id];
      const requestBody: FormData = new FormData();
      files.forEach((file: File) => {
        requestBody.append(FILES, file, file.name);
      });
      const filesInfo: ITransitionFileResponse[] =
        await FileApi.createExternalTransitionFiles(this.props.params, requestBody);
      const oldValue: string = fieldValue.value as string;
      const newValueArr: string[] = [];

      const oldExtra: string = fieldValue.extra;
      const newExtraArr: string[] = [];

      filesInfo.forEach((response: ITransitionFileResponse) => {
        newValueArr.push(response.fileName);
        newExtraArr.push(response.fileIdentity);
      });

      const newExtra: string = newExtraArr.join(',');
      const newValue: string = newValueArr.join(',');

      const extra: string = !isNil(oldExtra) && oldExtra.trim().length > 0
        ? oldExtra.concat(',', newExtra)
        : newExtra;

      const value: string = !isNil(oldValue) && oldValue.trim().length > 0
        ? oldValue.concat(',', newValue)
        : newValue;
      this.props.updateValue(id, {
        value,
        valid: true,
        extra,
      });
    } catch (e) {
      console.error(e.getErrorMessage());
      throw e;
    }
  }

    @autobind
    public blockExternalNavigation(e: Event): string {
      if (formHasBeenEdited(this.props.values.fields, this.props.meta.fields as ITabField[])) {
        e.preventDefault();
        // Internet Explorer needs text from this property to display custom message
        e.returnValue = WEBFORM_TEXT.NAVIGATION_PROMPT as any;
        return;
      }
    }

    @autobind
    public blockInternalNavigation(location: any): boolean | string {
      // if store.ui.isFetching is true (happens during submission), or form has not been edited, don't block navigation
      return ((this.props.ui.isFetching && this.props.ui.status === STATUS.Fetching)
        || !formHasBeenEdited(this.props.values.fields, this.props.meta.fields as ITabField[]))
          ? true
          : WEBFORM_TEXT.NAVIGATION_PROMPT;
    }

    public async componentDidMount(): Promise<void> {
        this.props.setSubmitted(false);
        this.props.fetchingRequest();
        try {
            await this.loadAllGroupUsers();
            // Get tabs and load into redux
            const tabs: ITab[] = await ExternalIncidentsApi.getFields(this.props.params).request();
            this.loadMetaInformation(tabs, this.props.params);
            // Get webform title from test
            const test: ITestRequestBody = await ExternalIncidentsApi.getTest(this.props.params).request();
            this.props.addFormTitle(test.name);
            // if we have any rule-driven fields we need to figure out their
            // initial visibility state
            if (this.props.ui.hasVisibilityRules) {
                this.getVisibilityRules();
            }
            this.getSettings();
            this.props.fetchingSuccess();
        } catch (e) {
            console.error(e);
            this.props.fetchingFailure();
            this.props.history.push('/error');
        }
        window.addEventListener('beforeunload', this.blockExternalNavigation);
    }

    public componentWillUnmount(): void {
      window.removeEventListener('beforeunload', this.blockExternalNavigation);
      URL.revokeObjectURL(this.props.ui.logoUrl);
    }

    @autobind
    public componentDidUpdate(prevProps: any): void {
        if (this.props.ui.hasVisibilityRules) {
            const prevPayload: IFieldData[] = VisibilityUtils.formatDataForVisibilityRules(this.props.values.fields);
            const currPayload: IFieldData[] = VisibilityUtils.formatDataForVisibilityRules(prevProps.values.fields);
            if (!isEqual(prevPayload, currPayload)) {
                this.getVisibilityRules();
            }
        }
    }

    @autobind
    public async loadAllGroupUsers(): Promise<void> {
      this.props.addGroupUsers(await UserApi.getGroupUsersExternal().request());
    }

    public render(): JSX.Element {
      return (
          <div data-test-id={DATA_TEST_ID.WEBFORM}>
            <WebformView
              {...this.props}
              handleSubmit={this.handleSubmit}
              handleCaptchaVerify={this.handleCaptchaVerify}
              handleCaptchaExpiration={this.handleCaptchaExpiration}
              formTitle={this.props.meta.title}
              downloadFile={this.downloadFile}
              saveTemplateFile={this.saveTemplateFile}
              isExternal
              groupUsers={this.props.groupUsers}
              saveTemplateFiles={this.saveTemplateFiles}
            />
            <Prompt
              message={this.blockInternalNavigation}
            />
          </div>
        );
    }
    }
